import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, RequireAuth, RequireFinancials } from "@/lib/auth";
import NotFound from "@/pages/not-found";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Website from "@/pages/Website";
import WebsiteQr from "@/pages/WebsiteQr";
import BuyerNetwork from "@/pages/BuyerNetwork";
import CompanyDetail from "@/pages/CompanyDetail";
import ClientDetail from "@/pages/ClientDetail";
import ContactDetail from "@/pages/ContactDetail";
import COI from "@/pages/COI";
import COIDetail from "@/pages/COIDetail";
import Clients from "@/pages/Clients";
import Prospects from "@/pages/Prospects";
import CapitalProviders from "@/pages/CapitalProviders";
import CapitalProviderDetail from "@/pages/CapitalProviderDetail";
import Contacts from "@/pages/Contacts";
import Settings from "@/pages/Settings";
import Tasks from "@/pages/Tasks";
import Projects from "@/pages/Projects";
import Campaigns from "@/pages/Campaigns";
import MailboxHealth from "@/pages/MailboxHealth";
import MassOutreach from "@/pages/MassOutreach";
import CampaignDetail from "@/pages/CampaignDetail";
import CampaignComposer from "@/pages/CampaignComposer";
import NewsletterIssues from "@/pages/NewsletterIssues";
import NewsletterComposer from "@/pages/NewsletterComposer";
import NewsletterLists from "@/pages/NewsletterLists";
import ProjectDetail from "@/pages/ProjectDetail";
import ProjectPrint from "@/pages/ProjectPrint";
import More from "@/pages/More";
import Notifications from "@/pages/Notifications";
import Archived from "@/pages/Archived";
import { useEmailSync } from "@/hooks/useEmailSync";

function Router() {
  // Global keyboard shortcuts were removed on 2026-09-01. Ctrl+A, Ctrl+B,
  // Ctrl+C and Ctrl+I hijacked select-all, bold, copy and italic, and the input
  // guard only covered <input> and <textarea>, so they fired inside rich-text
  // and contenteditable fields too.
  //
  // The two Caleb kept still work, and better than before:
  //   Cmd/Ctrl+K — owned solely by AppLayout now; the hook bound it a second
  //                time to focus the search input, so two handlers raced.
  //   Escape     — handled natively by Radix on every Dialog and Sheet. The
  //                hook's version clicked EVERY [aria-label="Close"] on the
  //                page, which could dismiss more than the thing in front of you.
  useEmailSync(); // Poll Microsoft Graph for new emails every 60s
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/">
        <RequireAuth><Dashboard /></RequireAuth>
      </Route>
      <Route path="/financials">
        <RequireFinancials><Dashboard tab="financials" /></RequireFinancials>
      </Route>
      {/* A company with no page of its own (COI-category firms): the ordinary company page. */}
      <Route path="/company/:id">{(params) => <Redirect to={`/companies/${params.id}`} />}</Route>
      <Route path="/companies/:id">
        <RequireAuth><CompanyDetail /></RequireAuth>
      </Route>
      <Route path="/buyers/:id">
        <RequireAuth><CompanyDetail /></RequireAuth>
      </Route>
      <Route path="/buyers">
        <RequireAuth><BuyerNetwork /></RequireAuth>
      </Route>
      <Route path="/coi/:id">
        <RequireAuth><COIDetail /></RequireAuth>
      </Route>
      <Route path="/coi">
        <RequireAuth><COI /></RequireAuth>
      </Route>
      <Route path="/clients/:id">
        <RequireAuth><ClientDetail /></RequireAuth>
      </Route>
      <Route path="/clients">
        <RequireAuth><Clients /></RequireAuth>
      </Route>
      <Route path="/prospects/:id">
        <RequireAuth><CompanyDetail /></RequireAuth>
      </Route>
      <Route path="/prospects">
        <RequireAuth><Prospects /></RequireAuth>
      </Route>
      <Route path="/capital-providers/:id">
        <RequireAuth><CapitalProviderDetail /></RequireAuth>
      </Route>
      <Route path="/capital-providers">
        <RequireAuth><CapitalProviders /></RequireAuth>
      </Route>
      <Route path="/projects/:id/print">
        <RequireAuth><ProjectPrint /></RequireAuth>
      </Route>
      <Route path="/projects/:id">
        <RequireAuth><ProjectDetail /></RequireAuth>
      </Route>
      <Route path="/projects">
        <RequireAuth><Projects /></RequireAuth>
      </Route>
      <Route path="/contacts/:id">
        <RequireAuth><ContactDetail /></RequireAuth>
      </Route>
      <Route path="/contacts">
        <RequireAuth><Contacts /></RequireAuth>
      </Route>
      <Route path="/settings">
        <RequireAuth><Settings /></RequireAuth>
      </Route>
      <Route path="/tasks">
        <RequireAuth><Tasks /></RequireAuth>
      </Route>
      <Route path="/campaigns/new">
        <RequireAuth><CampaignComposer /></RequireAuth>
      </Route>
      <Route path="/campaigns/:id/edit">
        <RequireAuth><CampaignComposer /></RequireAuth>
      </Route>
      <Route path="/campaigns/mailboxes">
        <RequireAuth><MailboxHealth /></RequireAuth>
      </Route>
      {/*
        Lead lists live inside Campaigns now, not as their own sidebar entry:
        a list is what a campaign is built from, so splitting them put one
        workflow in two places. MUST stay above /campaigns/:id, which would
        otherwise match "lists" as an id.
      */}
      <Route path="/campaigns/lists">
        <RequireAuth><MassOutreach /></RequireAuth>
      </Route>
      {/* Kept as a redirect: the old route is in bookmarks. */}
      <Route path="/mass-outreach">
        <RequireAuth><Redirect to="/campaigns/lists" /></RequireAuth>
      </Route>
      {/*
        Newsletter routes MUST stay above /campaigns/:id, which would otherwise
        match "newsletter" as a campaign id.
      */}
      <Route path="/campaigns/newsletter/new">
        <RequireAuth><NewsletterComposer /></RequireAuth>
      </Route>
      <Route path="/campaigns/newsletter/:id/edit">
        <RequireAuth><NewsletterComposer /></RequireAuth>
      </Route>
      {/*
        A rule-based segment over the buyer network, not the Mass Outreach
        cold-lead lists at /campaigns/lists — deliberately a separate page
        rather than a second tab there (see task-14-brief.md). Must stay
        above /campaigns/:id for the same reason as the other newsletter
        routes. /campaigns/:id/edit is 3 segments too, but its third segment
        must be the literal "edit", so "/campaigns/newsletter/lists" (third
        segment "lists") never matches it.
      */}
      <Route path="/campaigns/newsletter/lists">
        <RequireAuth><NewsletterLists /></RequireAuth>
      </Route>
      <Route path="/campaigns/newsletter">
        <RequireAuth><NewsletterIssues /></RequireAuth>
      </Route>
      <Route path="/campaigns/:id">
        <RequireAuth><CampaignDetail /></RequireAuth>
      </Route>
      <Route path="/campaigns">
        <RequireAuth><Campaigns /></RequireAuth>
      </Route>
      {/*
        Reminders live in Tasks now — a task with an alarm IS a reminder, so a
        second page over the same rows is a second place to look. Kept as a
        redirect rather than deleted: the route is in reminder emails, in push
        notification deep links, and in people's bookmarks.
      */}
      <Route path="/reminders">
        <RequireAuth><Redirect to="/tasks?view=reminders" /></RequireAuth>
      </Route>
      {/* /website and /website/valuations are the same view: valuations is the
          default tab, and both spellings are linkable. */}
      <Route path="/website">
        <RequireAuth><Website tab="valuation" /></RequireAuth>
      </Route>
      <Route path="/website/valuations">
        <RequireAuth><Website tab="valuation" /></RequireAuth>
      </Route>
      <Route path="/website/subscribers">
        <RequireAuth><Website tab="subscriber" /></RequireAuth>
      </Route>
      <Route path="/website/buyers">
        <RequireAuth><Website tab="buyer" /></RequireAuth>
      </Route>
      <Route path="/website/qr">
        <RequireAuth><WebsiteQr /></RequireAuth>
      </Route>
      <Route path="/notifications">
        <RequireAuth><Notifications /></RequireAuth>
      </Route>
      <Route path="/archived">
        <RequireAuth><Archived /></RequireAuth>
      </Route>
      <Route path="/more">
        <RequireAuth><More /></RequireAuth>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <SonnerToaster position="top-right" richColors />
          <Router />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
